//CRN_20251218 - ⚠️ ngOnInit() called for components/directives & not services ⚠️

import { BooleanResultDTO } from 'src/app/core/interfaces/general/boolean-result';
import { catchError, forkJoin, lastValueFrom, map, Observable, of, switchMap, tap } from 'rxjs';
import { ChildCreditLimitBreachDTO } from 'src/app/core/models/system/company/child-credit-limit-breach.dto';
import { ChildrenCreditLimitBreachRequestDTO } from 'src/app/core/models/system/company/children-credit-limit-breach-request.dto';
import { CompanyDTO } from 'src/app/core/models/system/company/company.dto';
import { CompanyIdDescriptionDTO } from 'src/app/core/models/system/company/companyIdDescription.dto';
import { CompanySsoIdsDTO } from 'src/app/core/models/system/company/company-sso-ids.dto';
import CustomStore from 'devextreme/data/custom_store';
import { DecimalResultDTO } from 'src/app/core/interfaces/general/decimal-result';
import { DecryptAesClientSide } from 'src/app/core/encryption';
import { EncryptionService } from '../../general/encryption-service';
import { environment } from 'src/environments/environment';
import { eSimApiPaths } from 'src/app/core/consts';
import { ExceptionsService } from '../../general/exceptions-service';
import { Guid } from 'guid-typescript';
import { HttpClientService } from '../../general/http-client-service';
import { IdAndDescriptionDTO } from 'src/app/core/interfaces/general/id-description';
import { Injectable } from '@angular/core';
import { IntegerResultDTO } from 'src/app/core/interfaces/general/integer-result';
import { StringResultDTO } from 'src/app/core/interfaces/general/string-result';

@Injectable({ providedIn: 'root' })

export class CompaniesService {

  public allSsoIds   : CompanySsoIdsDTO[] | undefined;
  public ssoIdsLoaded: boolean=false;
  
  constructor(
    private encryptionService: EncryptionService,
    private exceptionsService: ExceptionsService,
    private httpClientService: HttpClientService
  ) { }

  public canCreateCompanyUnder(parentInviewCompanyId: number): Observable<BooleanResultDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>parentInviewCompanyId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const parentInviewCompanyIdEncSS=encResult.result;

                            return this.httpClientService.get<BooleanResultDTO>(environment.eSimApi
                                                                                + eSimApiPaths.Companies
                                                                                + "/"
                                                                                + eSimApiPaths.CompaniesCanCreateCompanyUnder
                                                                                + "?parentInviewCompanyIdEncSS=" + parentInviewCompanyIdEncSS);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public cease(id: number, confirmCompanyName: string): Observable<any> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>id, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const idEncSS=encResult.result;

                            return this.httpClientService.put(environment.eSimApi
                                                              + eSimApiPaths.Companies
                                                              + "/"
                                                              + eSimApiPaths.CompaniesCease
                                                              + "?idEncSS=" + idEncSS, undefined, { confirmCompanyName: confirmCompanyName });
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public cleanGuid(company: CompanyDTO) {
    try {
      company.guid=company.guid != undefined && <Guid>company.guid["value"] != undefined ? company.guid["value"] : company.guid;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public delete(id: number): Observable<CompanyDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>id, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const idEncSS=encResult.result;
                            
                            return this.httpClientService.delete(environment.eSimApi +
                                                                 eSimApiPaths.Companies +
                                                                 "?idEncSS=" + idEncSS, undefined); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getById(id: number): Observable<CompanyDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>id, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const idEncSS=encResult.result;
                            
                            return this.httpClientService.getById(environment.eSimApi +
                                                                  eSimApiPaths.Companies +
                                                                  "?idEncSS=" + idEncSS, undefined); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getChildrenCreditLimitBreaches(request: ChildrenCreditLimitBreachRequestDTO): Observable<ChildCreditLimitBreachDTO[]> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(JSON.stringify(request), true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const requestEncSS=encResult.result;

                            return this.httpClientService.post(environment.eSimApi +
                                                               eSimApiPaths.Companies + "/" +
                                                               eSimApiPaths.CompaniesGetChildrenCreditLimitBreaches +
                                                               "?requestEncSS=" + requestEncSS, {});
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getForLoggedInUser(userId: number): Observable<CompanyDTO[]> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const userIdEncSS=encResult.result;
                            
                            return this.httpClientService.get<CompanyDTO[]>(environment.eSimApi +
                                                                            eSimApiPaths.Companies + "/" +
                                                                            eSimApiPaths.CompaniesForUser +
                                                                            "?userIdEncSS=" + userIdEncSS); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  // public getIdAndDescription(): Observable<IdAndDescriptionDTO[]> {
  //   try {
  //     return this.httpClientService.get<CompanyDTO[]>(environment.eSimApi + eSimApiPaths.Companies)
  //     .pipe(
  //       map(x => x.map(this.idAndDescriptionConverter))
  //     );
  //   }
  //   catch(e) {
  //     this.exceptionsService.handleException(<Error>e);
  //     return of();
  //   }
  // }

  public getHierarchyIdAndDescriptionForUser(userId       : number | undefined,
                                             hasFeature   : string | undefined,
                                             includeCeased: boolean=false) {
    try {
      return forkJoin({
               hasFeatureEncResult : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>(hasFeature ?? -1)),
               userEncResult       : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>(userId ?? -1), true)
             })
             .pipe(
                switchMap(({hasFeatureEncResult, userEncResult}) => {
                            const hasFeatureEnc=hasFeatureEncResult.result;
                            const userIdEncSS  =userEncResult.result;
                            
                            return this.httpClientService.get<CompanyIdDescriptionDTO[]>(
                                        environment.eSimApi
                                        + eSimApiPaths.Companies
                                        + "/"
                                        + eSimApiPaths.HierarchyIdDescription
                                        + "?userIdEncSS=" + userIdEncSS
                                        + "&hasFeatureEnc=" + hasFeatureEnc
                                        + (includeCeased ? "&includeCeased=true" : ""))
                                   .pipe(
                                     map(x => x.map(this.idAndDescriptionConverterHierarchy))
                                   ); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIdAndDescriptionForLoggedInUser(userId: number): Observable<IdAndDescriptionDTO[]> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const userIdEncSS=encResult.result;
                            
                            return this.httpClientService.get<CompanyDTO[]>(environment.eSimApi +
                                                                            eSimApiPaths.Companies + "/" +
                                                                            eSimApiPaths.CompaniesForUser +
                                                                            "?userIdEncSS=" + userIdEncSS)
                                   .pipe(
                                     map(x => x.map(this.idAndDescriptionConverter))
                                   ); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getIdByCompanyName(companyName: string): Observable<IntegerResultDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(companyName, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyNameEncSS=encResult.result;
                            
                            return this.httpClientService.getByDescription(environment.eSimApi +
                                                                           eSimApiPaths.Companies + "/" +
                                                                           eSimApiPaths.GetIdByCompanyName +
                                                                           "?companyNameEncSS=", companyNameEncSS); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getInviewCompanyId(companyId: number): Observable<IntegerResultDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyIdEncSS=encResult.result;

                            return this.httpClientService.getByDescription(environment.eSimApi +
                                                                           eSimApiPaths.Companies + "/" +
                                                                           eSimApiPaths.GetInviewCompanyId +
                                                                           "?companyIdEncSS=", companyIdEncSS);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getParentCreditLimit(childId: number): Observable<DecimalResultDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>childId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const childIdEncSS=encResult.result;

                            return this.httpClientService.get<DecimalResultDTO>(environment.eSimApi +
                                                                                eSimApiPaths.Companies + "/" +
                                                                                eSimApiPaths.CompaniesParentCreditLimit +
                                                                                "?childIdEncSS=" + childIdEncSS);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getSuperHierarchyIdAndDescriptionForUser(userId       : number | undefined,
                                                  hasFeature   : string | undefined,
                                                  includeCeased: boolean=false) {
    try {
      return forkJoin({
               hasFeatureEncResult : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>(hasFeature ?? -1)),
               userEncResult       : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>(userId ?? -1), true)
             })
             .pipe(
                switchMap(({hasFeatureEncResult, userEncResult}) => {
                            const hasFeatureEnc=hasFeatureEncResult.result;
                            const userIdEncSS  =userEncResult.result;
                            
                            return this.httpClientService.get<CompanyIdDescriptionDTO[]>(
                                        environment.eSimApi
                                        + eSimApiPaths.Companies
                                        + "/"
                                        + eSimApiPaths.CompaniesSuperHierarchyIdDescription
                                        + "?userIdEncSS=" + userIdEncSS
                                        + "&hasFeatureEnc=" + hasFeatureEnc
                                        + (includeCeased ? "&includeCeased=true" : ""))
                                   .pipe(
                                     map(x => x.map(this.idAndDescriptionConverterHierarchy))
                                   ); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  private idAndDescriptionConverter(company: CompanyDTO) {
    return {id: company.id, description: company.companyName};
  }

  private idAndDescriptionConverterHierarchy(company: CompanyIdDescriptionDTO) {
    return {id: company.id, description: company.companyName};
  }

  // public idAndDescriptionLookupDataSource(): {} {
  //   try {
  //     return {
  //       store: new CustomStore({
  //         key: "id",
  //         loadMode: "raw",
  //         load: () => {
  //           return lastValueFrom(this.getIdAndDescription());
  //         }
  //       }),
  //       sort: "description"
  //   };
  //   }
  //   catch(e) {
  //     this.exceptionsService.handleException(<Error>e);
  //     return {};
  //   }
  // }

  public idAndDescriptionLookupDataSourceForLoggedInUser(userID: number): {} {
    try {
      return {
        store: new CustomStore({
          key: "id",
          loadMode: "raw",
          load: () => {
            return lastValueFrom(this.getIdAndDescriptionForLoggedInUser(userID));
          }
        }),
        sort: "description"
    };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return {};
    }
  }
  
  public killCompany(guid: Guid): Observable<BooleanResultDTO> {
    try {
      return this.httpClientService.delete(environment.eSimApi +
                                           eSimApiPaths.Companies + "/" +
                                           eSimApiPaths.CompanyKillCompany +
                                           "?guid=" + guid);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public post(company: CompanyDTO): Observable<CompanyDTO> {
    try {
      //JSON.stringify(company)
      this.cleanGuid(company);
      
      return this.encryptionService.encrypt_AesServerSideForWeb(JSON.stringify(company), true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyEncSS=encResult.result;
                            
                            return this.httpClientService.post(environment.eSimApi +
                                                               eSimApiPaths.Companies +
                                                               "?companyEncSS=" + companyEncSS, {}); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public put(company: CompanyDTO): Observable<CompanyDTO> {
    try {
      //JSON.stringify(company)
      this.cleanGuid(company);
      
      return this.encryptionService.encrypt_AesServerSideForWeb(JSON.stringify(company), true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const companyEncSS=encResult.result;
                            
                            return this.httpClientService.put(environment.eSimApi +
                                                              eSimApiPaths.Companies +
                                                              "?id=" + company.id +
                                                              "&companyEncSS=" + companyEncSS, undefined, {});
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
      
  public save(company: CompanyDTO): Observable<any> {
    try {
      //JSON.stringify(company);
      if (company.id && <number>company.id>0)
        return this.put(company);
  
      return this.post(company);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  //CRN_20261021 - Retired
  // public uncease(id: number): Observable<any> {
  //   try {
  //     return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>id, true)
  //            .pipe(
  //               switchMap((encResult: StringResultDTO) => {
  //                           const idEncSS=encResult.result;
  //
  //                           return this.httpClientService.put(environment.eSimApi
  //                                                             + eSimApiPaths.Companies
  //                                                             + "/"
  //                                                             + eSimApiPaths.CompaniesUncease
  //                                                             + "?idEncSS=" + idEncSS, undefined, undefined);
  //                        })
  //                 );
  //   }
  //   catch(e) {
  //     this.exceptionsService.handleException(<Error>e);
  //     return of();
  //   }
  // }

  public undelete(id: number): Observable<any> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>id, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const idEncSS=encResult.result;

                            return this.httpClientService.put(environment.eSimApi +
                                                              eSimApiPaths.Companies + "/" +
                                                              eSimApiPaths.CompaniesUndelete +
                                                              "?idEncSS=" + idEncSS, undefined, undefined);
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  //#region SSO

  private encryptionConverterSsoIds(companySsoIds: CompanySsoIdsDTO) {
    return {
             domain              : DecryptAesClientSide(companySsoIds.domain),
             id                  : companySsoIds.id,
             ssoMicrosoft        : companySsoIds.ssoMicrosoft,
             ssoMicrosoftTenantID: DecryptAesClientSide(companySsoIds.ssoMicrosoftTenantID ?? "")
           };
  }
  
  public getEncryptedSsoIds(): Observable<CompanySsoIdsDTO[]> {
    try {
      return this.httpClientService.get<CompanySsoIdsDTO[]>(environment.eSimApi +
                                                            eSimApiPaths.Companies + "/" +
                                                            eSimApiPaths.CompaniesGetSsoIdsEncrypted)
      .pipe(
        map(x => x.map(this.encryptionConverterSsoIds))
      );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public getSsoId(domain: string, defaultSsoId: string="common"): string | undefined {
    try {
      domain=domain.toLowerCase();
      
      let companySsoId: CompanySsoIdsDTO | undefined=this.allSsoIds?.find(x => x.domain==domain);
      
      if (companySsoId==undefined || !companySsoId.ssoMicrosoft)
        return undefined;
      
      if (!companySsoId.ssoMicrosoftTenantID)
        return defaultSsoId;
      
      return companySsoId.ssoMicrosoftTenantID;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return defaultSsoId;
    }
  }
    
  public refreshSsoIds(): Observable<any> {
    try {
      if (!this.ssoIdsLoaded) {
        return this.getEncryptedSsoIds()
               .pipe(
                 catchError((error) => {
                  this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(error)));
                  return of([]);
                 }),
                 tap((data) => {
                   this.allSsoIds=data.map((item: CompanySsoIdsDTO) => ({
                     ...item,
                     domain: (item.domain ?? "").toLowerCase()
                   }));

                   this.ssoIdsLoaded=true;
                 })
                );
      }

      return of([]);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of([]);
    }
  }

  //#endregion
}
